import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import classNames from "classnames";

import { Logo } from "@/components/Logo";
import { CONTACT_INFO } from "@/constants/site";
import { navProducts, navSupport } from "@/data/siteContent";
import { useT } from "@/i18n";
import { pathNames } from "@/router/paths";

import { FooterMobileNav } from "./components/FooterMobileNav";
import { mobileCardClass } from "./mobileCardClass";

const linkClass =
  "flex min-h-[44px] cursor-pointer items-center justify-between rounded-lg px-1 text-[20px] transition active:bg-yoyo-primary/5 md:min-h-0 md:block md:rounded-none md:px-0 md:active:bg-transparent";

function NavItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div role="button" tabIndex={0} className={linkClass} onClick={onClick}>
      <span>{label}</span>
    </div>
  );
}

function NavList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-0.5 md:space-y-2">{children}</div>;
}

function BrandBlock({
  className,
  showCopyright = true,
}: {
  className?: string;
  showCopyright?: boolean;
}) {
  const lt = useT();

  return (
    <div
      className={classNames(
        mobileCardClass,
        "px-4 py-6 md:rounded-none md:border-0 md:bg-transparent md:px-0 md:shadow-none",
        className,
      )}
    >
      {/* 品牌 Logo */}
      <Logo size="lg" />
      {/* 联系方式 */}
      <div className="mt-[28px] flex flex-col gap-[8px] text-[20px]">
        <p className="text-yoyo-text">
          <span className="mr-2">{lt("common.hotline")}:</span>
          {CONTACT_INFO.hotline}
        </p>
        <p className="text-yoyo-text">
          <span className="mr-2">Email: </span>
          {CONTACT_INFO.email}
        </p>
        <p className="text-yoyo-text">
          <span className="mr-2">{lt("common.address")}: </span>
          {lt("contact.address")}
        </p>
      </div>
      {/* 版权信息（桌面端显示在品牌区） */}
      {showCopyright && (
        <p className="mt-12.5 text-[16px] text-yoyo-secondary/60 md:text-[16px]">
          {CONTACT_INFO.copyright}
        </p>
      )}
    </div>
  );
}

export function Footer() {
  const lt = useT();
  const navigate = useNavigate();

  return (
    <footer className="bg-yoyo-footer">
      <div className="lg:px-8 md:px-6 py-6 max-w-site mx-auto px-4">
        {/* 移动端：品牌 + 底部导航 + 版权 */}
        <div className="md:hidden">
          <BrandBlock showCopyright={false} />
          <FooterMobileNav />
          {/* 版权信息（移动端底部居中） */}
          <p className="mt-8 text-center text-[16px] leading-relaxed text-yoyo-secondary/60">
            {CONTACT_INFO.copyright}
          </p>
        </div>

        {/* 桌面 / 平板：品牌联系 : 产品+关于 : 支持 */}
        <div className="hidden w-full md:flex md:justify-between md:items-center  ">
          <BrandBlock className="flex-1" />

          <div className="flex justify-between ml-40 gap-[60px] md:w-[400px] md:gap-[20px] md:ml-20 lg:w-[500px]">
            {/* 产品 + 关于 */}
            <div className="flex flex-1 flex-col">
              <h4 className="mb-2 text-[24px] font-bold text-yoyo-text">
                {lt("nav.products")}
              </h4>
              <NavList>
                {navProducts.map((item) => (
                  <NavItem
                    key={item.path + item.labelKey}
                    label={lt(item.labelKey)}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </NavList>
              <h4
                className="mt-[10px] cursor-pointer text-[24px] font-bold text-yoyo-text"
                onClick={() => navigate(pathNames.about)}
              >
                {lt("nav.about")}
              </h4>
            </div>

            {/* 支持（两列链接） */}
            <div className="flex flex-2 flex-col">
              <h4 className="cursor-pointer text-[24px] font-bold text-yoyo-text">
                {lt("nav.support")}
              </h4>
              <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 md:gap-x-2">
                {navSupport.map((item) => (
                  <NavItem
                    key={item.path}
                    label={lt(item.labelKey)}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <p className="mt-6 text-center text-[16px] text-yoyo-secondary/60">
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            渝ICP备14005406号
          </a>
        </p>
      </div>
    </footer>
  );
}
